import React, { useContext, useEffect, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { colors } from '@/shared/tokens/colors';
import { Header } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { data_from_server_get, data_from_server_post } from '@/shared/http/client';
import { getColorForUsecaseStatus } from '@/features/tasks/components/HomePage/HomepageTableView';
import { DEFAULT_CLIENT_ID } from '@/config/client';
import { buildPieSlicePercentLabel } from '@/shared/utils/pieChartUtils';
import AnalyticsChartCard from '@/features/analytics/components/AnalyticsChartCard.jsx';
import TopStatCards from '@/features/analytics/components/TopStatCards.jsx';
import ActivityLogTable from '@/features/analytics/components/ActivityLogTable.jsx';

const CHART_BACKGROUND_BAR_FILL = '#EBEBEB';
const CHART_GRID_STROKE = '#F0F0F0';
const AXIS_TICK_COLOR = '#6B7280';

const TOOLTIP_STYLE = {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    border: '1px solid #E5E7EB',
    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
    padding: '8px 12px',
};

const TASK_DISTRIBUTION_COLORS = {
    deployed: colors.green,
    undeployed: colors.buttonsBlue,
};

const INITIAL_METRICS = {
    totalApps: 0,
    totalUseCases: 0,
    totalDatasets: 0,
    totalBenchmarks: 0,
    deployedUseCases: 0,
    undeployedUseCases: 0,
};

const PieLegend = ({ items }) => (
    <div
        style={{
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: 18,
            marginTop: 8,
        }}
    >
        {items.map((item) => (
            <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                    style={{
                        width: 10,
                        height: 10,
                        borderRadius: 2,
                        background: item.color,
                        display: 'inline-block',
                    }}
                />
                <span style={{ color: '#374151', fontSize: 13 }}>
                    {item.name}
                    <span style={{ color: '#9CA3AF', marginLeft: 6 }}>{item.value}</span>
                </span>
            </div>
        ))}
    </div>
);

const UsabilityDashboard = ({ style } = {}) => {
    const { activityLog, setActivityLog } = useContext(GlobalContext);

    const [loadingStatusDistribution, setLoadingStatusDistribution] = useState(false);
    const [loadingActivities, setLoadingActivities] = useState(false);
    const [loadingDashboardStats, setLoadingDashboardStats] = useState(false);
    const [loadingTrainingTrends, setLoadingTrainingTrends] = useState(false);
    const [statusDistribution, setStatusDistribution] = useState([]);
    const [metrics, setMetrics] = useState(INITIAL_METRICS);
    const [trainingTrends, setTrainingTrends] = useState([]);

    useEffect(() => {
        setLoadingStatusDistribution(true);
        data_from_server_post(
            'statistics/usecase-status-distribution',
            DEFAULT_CLIENT_ID,
            (response) => {
                setStatusDistribution(response.distribution || []);
                setLoadingStatusDistribution(false);
            },
            () => setLoadingStatusDistribution(false),
        );

        setLoadingActivities(true);
        const params = new URLSearchParams({ client_id: DEFAULT_CLIENT_ID });
        data_from_server_get(
            `statistics/activities?${params}`,
            (response) => {
                setActivityLog(response.activities || []);
                setLoadingActivities(false);
            },
        );

        setLoadingDashboardStats(true);
        data_from_server_post(
            'statistics/dashboard-statistics',
            DEFAULT_CLIENT_ID,
            (response) => {
                setMetrics((prev) => ({
                    ...prev,
                    totalApps: response.total_apps,
                    totalUseCases: response.total_usecases,
                    totalDatasets: response.total_datasets,
                    totalBenchmarks: response.total_benchmarks,
                    deployedUseCases: response.deployed_usecases,
                    undeployedUseCases: response.undeployed_usecases,
                }));
                setLoadingDashboardStats(false);
            },
            () => setLoadingDashboardStats(false),
        );

        setLoadingTrainingTrends(true);
        data_from_server_post(
            'statistics/training-trends',
            DEFAULT_CLIENT_ID,
            (response) => {
                if (response.success && response.trends) {
                    setTrainingTrends(response.trends);
                }
                setLoadingTrainingTrends(false);
            },
            () => setLoadingTrainingTrends(false),
        );
    }, [setActivityLog]);

    const taskDistributionRaw = [
        {
            name: 'Deployed',
            value: metrics.deployedUseCases ?? 0,
            color: TASK_DISTRIBUTION_COLORS.deployed,
        },
        {
            name: 'Undeployed',
            value: metrics.undeployedUseCases ?? 0,
            color: TASK_DISTRIBUTION_COLORS.undeployed,
        },
    ];
    const taskDistributionData = taskDistributionRaw.filter((d) => d.value > 0);
    const totalTaskDistribution = taskDistributionRaw.reduce((sum, d) => sum + d.value, 0);

    const hasStatusData = statusDistribution && statusDistribution.length > 0;
    const hasTaskDistribution = totalTaskDistribution > 0;
    const hasTrainingTrends = trainingTrends && trainingTrends.length > 0;

    return (
        <div className="page-content-shell" style={{ ...style }}>
            <div className="fs-unmask" style={{ padding: '24px 0 32px 0' }}>
                <Header
                    showBreadcrumb={false}
                    showBackButton={false}
                    showActionBar={false}
                    title="Analytics"
                    subtitle="Track project activity, task status, and training trends across your workspace."
                />
            </div>

            <TopStatCards metrics={metrics} loading={loadingDashboardStats} />

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 1fr)',
                    gap: 16,
                    marginBottom: 24,
                }}
            >
                <AnalyticsChartCard
                    title="Task Status Distribution"
                    loading={loadingStatusDistribution}
                    isEmpty={!hasStatusData}
                    emptyTitle="No tasks yet"
                    emptyDescription="Once you create tasks, the status distribution will appear here."
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={statusDistribution}
                            margin={{ top: 16, right: 16, left: -10, bottom: 0 }}
                        >
                            <CartesianGrid stroke={CHART_GRID_STROKE} strokeWidth={1} vertical={false} />
                            <XAxis
                                dataKey="name"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                                interval={0}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                                allowDecimals={false}
                            />
                            <RechartsTooltip
                                cursor={{ fill: 'rgba(15, 23, 42, 0.04)' }}
                                contentStyle={TOOLTIP_STYLE}
                                formatter={(value) => [`${value}`, 'Tasks']}
                            />
                            <Bar
                                dataKey="count"
                                name="Tasks"
                                radius={[6, 6, 0, 0]}
                                barSize={42}
                                background={{ fill: CHART_BACKGROUND_BAR_FILL, radius: 6 }}
                                isAnimationActive
                                animationDuration={700}
                            >
                                {statusDistribution.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={getColorForUsecaseStatus(entry.name) || colors.gray}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </AnalyticsChartCard>

                <AnalyticsChartCard
                    title="Task Distribution"
                    loading={loadingDashboardStats}
                    isEmpty={!hasTaskDistribution}
                    emptyTitle="Nothing to chart"
                    emptyDescription="Deployment breakdown will appear once tasks exist."
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                        }}
                    >
                        <ResponsiveContainer width="100%" height="80%">
                            <PieChart>
                                <Pie
                                    data={taskDistributionData}
                                    cx="50%"
                                    cy="50%"
                                    outerRadius={90}
                                    paddingAngle={2}
                                    dataKey="value"
                                    stroke="none"
                                    labelLine={false}
                                    label={buildPieSlicePercentLabel(
                                        taskDistributionData.map((d) => d.value),
                                        { fill: 'white', fontSize: 12 },
                                    )}
                                >
                                    {taskDistributionData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <RechartsTooltip
                                    contentStyle={TOOLTIP_STYLE}
                                    formatter={(value, name) => [`${value}`, name]}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                        <PieLegend items={taskDistributionRaw} />
                    </div>
                </AnalyticsChartCard>
            </div>

            {/* <div style={{ marginBottom: 24 }}>
                <AnalyticsChartCard
                    title="Training Activity Trends"
                    loading={loadingTrainingTrends}
                    isEmpty={!hasTrainingTrends}
                    emptyTitle="No training activity yet"
                    emptyDescription="Trends will appear once trainings have been kicked off."
                >
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={trainingTrends}
                            margin={{ top: 16, right: 24, left: -10, bottom: 0 }}
                        >
                            <CartesianGrid stroke={CHART_GRID_STROKE} strokeWidth={1} vertical={false} />
                            <XAxis
                                dataKey="date"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                            />
                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                tick={{ fill: AXIS_TICK_COLOR, fontSize: 12 }}
                                allowDecimals={false}
                            />
                            <RechartsTooltip contentStyle={TOOLTIP_STYLE} />
                            <Line
                                type="monotone"
                                dataKey="count"
                                stroke={colors.buttonsBlue}
                                strokeWidth={2}
                                dot={{ r: 3, strokeWidth: 0, fill: colors.buttonsBlue }}
                                activeDot={{ r: 5 }}
                                name="Total Trainings"
                            />
                            <Line
                                type="monotone"
                                dataKey="success"
                                stroke={colors.green}
                                strokeWidth={2}
                                dot={{ r: 3, strokeWidth: 0, fill: colors.green }}
                                activeDot={{ r: 5 }}
                                name="Successful Trainings"
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </AnalyticsChartCard>
            </div> */}

            <ActivityLogTable activityLog={activityLog} loading={loadingActivities} />
        </div>
    );
};

export default UsabilityDashboard;
